/** Port of `backend/src/services/tierAvailability.service.ts`. */

import type { DemoSubscription, DemoTier } from "./fixtures";

export interface TierWithAvailability {
  id: string;
  subscriptionId?: string;
  name: string;
  price: number;
  oldCsaMemberPrice: number;
  spots?: number;
  dependsOn?: string;
  takesFrom: string[];
  isAvailable: boolean;
}

export function takesFromIds(tier: DemoTier): string[] {
  return Array.isArray(tier.takesFrom) ? tier.takesFrom.map(String) : [];
}

export function buildTiersWithAvailability(
  tierList: DemoTier[],
  subList: DemoSubscription[],
  subscriberCountByTierId: Record<string, number>,
): TierWithAvailability[] {
  const tierIdToTier = new Map<string, DemoTier>();
  const subscriptionIdToTierIds = new Map<string, string[]>();
  for (const tier of tierList) {
    tierIdToTier.set(tier._id, tier);
    const existing = subscriptionIdToTierIds.get(tier.subscriptionId) ?? [];
    existing.push(tier._id);
    subscriptionIdToTierIds.set(tier.subscriptionId, existing);
  }

  const count = (tierId: string) => subscriberCountByTierId[tierId] ?? 0;

  const subscriptionSpotsAvailable: Record<string, number> = {};
  for (const sub of subList) {
    const spots = sub.spots;
    if (spots == null) continue;
    const tierIdsInSub = subscriptionIdToTierIds.get(sub._id) ?? [];
    let used = 0;
    for (const tierId of tierIdsInSub) {
      const tier = tierIdToTier.get(tierId);
      if (!tier) continue;
      if (takesFromIds(tier).length > 0) continue;
      used += count(tierId);
    }
    for (const tier of tierList) {
      if (takesFromIds(tier).includes(sub._id)) used += count(tier._id);
    }
    subscriptionSpotsAvailable[sub._id] = Math.max(0, spots - used);
  }

  return tierList.map((tier) => {
    const takesFrom = takesFromIds(tier);
    const dependsOnId = tier.dependsOn;
    const tierSpots = tier.spots;

    let isAvailable: boolean;
    if (tierSpots != null) {
      const effectiveCapacity = tierSpots + (dependsOnId ? count(dependsOnId) : 0);
      isAvailable = count(tier._id) < effectiveCapacity;
    } else if (takesFrom.length > 0) {
      isAvailable = takesFrom.every(
        (subscriptionId) => (subscriptionSpotsAvailable[subscriptionId] ?? 0) > 0,
      );
    } else {
      const sub = subList.find((s) => s._id === tier.subscriptionId);
      const available =
        sub?.spots != null ? (subscriptionSpotsAvailable[tier.subscriptionId] ?? 0) : 0;
      isAvailable = available > 0;
    }

    return {
      id: tier._id,
      subscriptionId: tier.subscriptionId,
      name: tier.name ?? "",
      price: tier.price ?? 0,
      oldCsaMemberPrice: tier.oldCsaMemberPrice ?? 0,
      spots: tier.spots,
      dependsOn: dependsOnId,
      takesFrom,
      isAvailable,
    };
  });
}

/** Mirrors `spotsTaken` in GET /api/subscriptions. */
export function spotsTakenForSubscription(
  subscriptionId: string,
  tierList: DemoTier[],
  subscriberCountByTierId: Record<string, number>,
): number {
  let spotsTaken = 0;
  for (const tier of tierList) {
    const takesFrom = takesFromIds(tier);
    const count = subscriberCountByTierId[tier._id] ?? 0;
    if (tier.subscriptionId === subscriptionId && takesFrom.length === 0) {
      spotsTaken += count;
    }
    if (takesFrom.includes(subscriptionId)) {
      spotsTaken += count;
    }
  }
  return spotsTaken;
}
